import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { AlertCircle, CheckCircle, ArrowLeft, User, Mail, Building, MessageSquare } from "lucide-react";

export default function RequestAccessPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await api.post("/api/auth/request-access", {
        name,
        email,
        company: company || undefined,
        message: message || undefined,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF5]">
        <div className="w-full max-w-md px-8 text-center">
          <div className="bg-white rounded-xl shadow-sm border border-[#E8E4DA] p-10">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-display text-xl font-semibold text-[#2C2C28] mb-2">
              Request Submitted
            </h2>
            <p className="text-sm text-[#6B6B60] mb-6 leading-relaxed">
              Your access request has been sent to the administrator.
              You'll receive credentials once approved.
            </p>
            <Link
              to="/sign-in"
              className="inline-flex items-center gap-2 text-sm text-[#2D5016] font-medium hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF5]">
      <div className="w-full max-w-md px-8">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#2D5016] flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm tracking-tight">
                AV
              </span>
            </div>
            <span className="font-display text-3xl font-semibold tracking-tight text-[#2C2C28]">
              AutoVal
            </span>
          </div>
          <p className="text-[#6B6B60] text-sm">
            Request access to the platform
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E8E4DA] p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-[#3C3C36] mb-1.5"
              >
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9A90]" />
                <input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D4D0C8] rounded-lg text-sm text-[#2C2C28] placeholder-[#9C9A90] focus:border-[#2D5016] focus:ring-2 focus:ring-[#2D5016]/10 transition-all"
                  placeholder="John Smith"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="req-email"
                className="block text-sm font-medium text-[#3C3C36] mb-1.5"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9A90]" />
                <input
                  id="req-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D4D0C8] rounded-lg text-sm text-[#2C2C28] placeholder-[#9C9A90] focus:border-[#2D5016] focus:ring-2 focus:ring-[#2D5016]/10 transition-all"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="company"
                className="block text-sm font-medium text-[#3C3C36] mb-1.5"
              >
                Company{" "}
                <span className="text-[#9C9A90] font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9A90]" />
                <input
                  id="company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D4D0C8] rounded-lg text-sm text-[#2C2C28] placeholder-[#9C9A90] focus:border-[#2D5016] focus:ring-2 focus:ring-[#2D5016]/10 transition-all"
                  placeholder="Acme Capital"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-[#3C3C36] mb-1.5"
              >
                Message{" "}
                <span className="text-[#9C9A90] font-normal">(optional)</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#9C9A90]" />
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D4D0C8] rounded-lg text-sm text-[#2C2C28] placeholder-[#9C9A90] focus:border-[#2D5016] focus:ring-2 focus:ring-[#2D5016]/10 transition-all resize-none"
                  placeholder="I'm interested in evaluating a retail property..."
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#2D5016] text-white text-sm font-semibold rounded-lg hover:bg-[#3A6B1E] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Request Access"
              )}
            </button>
          </form>
        </div>

        {/* Back links */}
        <div className="mt-6 text-center space-y-2">
          <Link
            to="/sign-in"
            className="inline-flex items-center gap-2 text-sm text-[#6B6B60] hover:text-[#2D5016]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
          <p>
            <Link
              to="/welcome"
              className="text-sm text-[#9C9A90] hover:text-[#6B6B60] transition-colors"
            >
              Back to home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
